import { z } from 'zod';

/**
 * Global Cmd+K search across tasks / comments / chat messages.
 *
 * The endpoint (`GET /api/search`) returns a single payload with three
 * arrays — kept small (5 per kind by default) so the palette stays snappy
 * and the wire payload stays tiny.  Each hit carries enough joined
 * metadata to render the row and route the click without an extra
 * round-trip:
 *
 *   - task hits     → click opens the task modal (taskId)
 *   - comment hits  → click opens the parent task modal
 *   - chat hits     → click navigates to the topic URL and (eventually)
 *                     scrolls to the message
 *
 * Rank is the `ts_rank_cd` score; higher = better.  The server pre-sorts
 * descending so the FE can render the list as-is.
 *
 * Visibility: the server runs each branch under RLS — invalid hits never
 * appear (no need for FE-side filtering).
 */
export const UnifiedSearchQuerySchema = z.object({
  q: z.string().trim().min(1).max(200),
  /** Cap per-kind hits returned in the unified response (5 by default). */
  limit: z.coerce.number().int().min(1).max(20).optional(),
});
export type UnifiedSearchQuery = z.infer<typeof UnifiedSearchQuerySchema>;

const TaskHitSchema = z.object({
  kind: z.literal('task'),
  id: z.string().uuid(),
  key: z.string(),
  title: z.string(),
  projectId: z.string().uuid(),
  rank: z.number(),
});
export type SearchTaskHit = z.infer<typeof TaskHitSchema>;

const CommentHitSchema = z.object({
  kind: z.literal('comment'),
  id: z.string().uuid(),
  taskId: z.string().uuid(),
  taskKey: z.string(),
  taskTitle: z.string(),
  /** ts_headline snippet (~120 chars, `<mark>…</mark>` around hits). */
  snippet: z.string(),
  authorUserId: z.string().uuid(),
  createdAt: z.string().datetime(),
  rank: z.number(),
});
export type SearchCommentHit = z.infer<typeof CommentHitSchema>;

const ChatHitSchema = z.object({
  kind: z.literal('chat'),
  id: z.string().uuid(),
  topicId: z.string().uuid(),
  topicName: z.string(),
  topicKind: z.string(),
  teamId: z.string().uuid(),
  teamSlug: z.string(),
  snippet: z.string(),
  authorUserId: z.string().uuid(),
  createdAt: z.string().datetime(),
  rank: z.number(),
});
export type SearchChatHit = z.infer<typeof ChatHitSchema>;

export const UnifiedSearchResultsSchema = z.object({
  tasks: z.array(TaskHitSchema),
  comments: z.array(CommentHitSchema),
  messages: z.array(ChatHitSchema),
});
export type UnifiedSearchResults = z.infer<typeof UnifiedSearchResultsSchema>;
